import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid';
import Drawer from '@ui/drawer/Drawer';
import useDrawer from '@ui/drawer/DrawerRef';
import Dialogue from '@ui/dialog/Dialogue';
import useDialogue from '@ui/dialog/DialogueRef';
import FormButton from '@ui/button/FormButton';
import FormInputText from '@ui/form/input/FormInputText';
import Message from '@ui/message/Message';
import SelectList from '@ui/selector/SelectList';
import FormCheckbox from '@ui/checkbox/FormCheckbox';
import GroupedChecklist, { ChecklistGroup } from '@ui/checkbox/GroupedChecklist';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { BiScope, PropertyGroup } from '@app/model/bi/Bi';
import { useDeletePropertyGroup, usePropertyGroups, useSavePropertyGroup } from '@app/model/bi/service/BiHooks';
import { propertyCount, shortName } from '../Bi.utilities';
import { subtotalOverlaps } from './PickupMonitoring.utilities';

export type PropertyGroupsDrawerHandle = { open: (groupId: number | null) => void };

type Props = {
	// The BI scope of a company admin: every BI property of the company.
	scope: BiScope,
};

const NEW = 'new';
const MAX_TITLE = 40;

// The work that waits for an answer to "Discard the changes?": another group, a new group, or the close of the editor.
type Pending = { kind: 'switch', key: string } | { kind: 'close' };

const byTitle = (a: PropertyGroup, b: PropertyGroup) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' });
const sameIds = (a: number[], b: number[]) => a.length === b.length && [...a].sort((x, y) => x - y).every((id, i) => id === [...b].sort((x, y) => x - y)[i]);

// The editor of the Property groups (Editor 1 of the canvas): a company admin opens it with "Manage property groups" under
// the BI picker. The groups of the company are on the left (SelectList), the name and the properties of one group on the
// right, the properties by city (GroupedChecklist). Each group saves alone (Save group, Delete group): a save never changes
// another group, so two admins do not remove the changes of each other. With changes that are not saved, a move to another
// group, New group and each close action ask first.
const PropertyGroupsDrawer = React.forwardRef<PropertyGroupsDrawerHandle, Props>(({ scope }, ref) => {
	const drawer = useDrawer();
	const discardDialogue = useDialogue<void>();
	const deleteDialogue = useDialogue<void>();
	const toaster = useToaster();
	const groupsQuery = usePropertyGroups();
	const groups = useMemo(() => [...(groupsQuery.data?.groups ?? [])].sort(byTitle), [groupsQuery.data]);
	const [current, setCurrent] = useState<string>(NEW);
	const [selected, setSelected] = useState<number[]>([]);
	const pending = useRef<Pending | null>(null);
	// The name of the group of the last delete, for the message after the answer.
	const deleted = useRef('');
	const form = useForm<{ title: string, subtotalOnAll: boolean }>({ defaultValues: { title: '', subtotalOnAll: false } });
	const title = form.watch('title');
	// ROO2-314 item 7 (D36): a subtotal of Pick-up monitoring on All properties. No property can be in two marked groups.
	const subtotalOnAll = form.watch('subtotalOnAll');

	const editing = groups.find(g => String(g.id) === current) ?? null;
	const load = (key: string) => {
		const group = groups.find(g => String(g.id) === key) ?? null;
		setCurrent(group === null ? NEW : key);
		setSelected(group?.companyHotelIds ?? []);
		form.reset({ title: group?.title ?? '', subtotalOnAll: group?.subtotalOnAll ?? false });
	};

	useImperativeHandle(ref, () => ({
		// The editor opens on the group of the page, else on the first group, else on a new group.
		open: groupId => {
			load(groupId !== null && groups.some(g => g.id === groupId) ? String(groupId) : (groups[0] === undefined ? NEW : String(groups[0].id)));
			drawer.open();
		},
	}));

	const changed = editing === null
		? title.trim() !== '' || selected.length > 0
		: title.trim() !== editing.title || !sameIds(selected, editing.companyHotelIds) || subtotalOnAll !== editing.subtotalOnAll;
	const clean = title.trim();
	const duplicate = groups.some(g => g.id !== editing?.id && g.title.trim().toLowerCase() === clean.toLowerCase());
	// The properties of this group that another marked group holds too: the server refuses the save, so the drawer names them first.
	const overlaps = subtotalOverlaps(groups, editing?.id ?? null, selected, subtotalOnAll);
	const nameOf = (id: number) => {
		const property = scope.properties.find(p => p.companyHotelId === id);
		return property === undefined ? `Property ${id}` : shortName(property, scope.companyName);
	};
	const canSave = changed && clean.length > 0 && clean.length <= MAX_TITLE && !duplicate && selected.length > 0 && overlaps.length === 0;

	const ask = (next: Pending) => {
		if (!changed) {
			if (next.kind === 'close') drawer.close();
			else load(next.key);
			return;
		}
		pending.current = next;
		discardDialogue.open();
	};
	const discard = () => {
		const next = pending.current;
		pending.current = null;
		if (next?.kind === 'close') drawer.close();
		else if (next?.kind === 'switch') load(next.key);
	};

	const save = useSavePropertyGroup(
		answer => {
			const saved = answer.groups.find(g => g.title.trim().toLowerCase() === clean.toLowerCase());
			toaster.success('Property groups', `RoomPulse saved the group “${clean}”. Each BI user of ${scope.companyName} sees it.`);
			if (saved !== undefined) {
				setCurrent(String(saved.id));
				setSelected(saved.companyHotelIds);
				form.reset({ title: saved.title, subtotalOnAll: saved.subtotalOnAll });
			}
		},
		error => toaster.error('Property groups', error),
	);
	const remove = useDeletePropertyGroup(
		answer => {
			toaster.success('Property groups', `RoomPulse deleted the group “${deleted.current}”.`);
			const first = [...answer.groups].sort(byTitle)[0];
			setCurrent(first === undefined ? NEW : String(first.id));
			setSelected(first?.companyHotelIds ?? []);
			form.reset({ title: first?.title ?? '', subtotalOnAll: first?.subtotalOnAll ?? false });
		},
		error => toaster.error('Property groups', error),
	);

	// The properties by city: the city with the most properties first, then by name; the properties of a city by name.
	const checklist: ChecklistGroup[] = useMemo(() => {
		const cities = new Map<string, BiScope['properties']>();
		scope.properties.forEach(p => cities.set(p.area, [...(cities.get(p.area) ?? []), p]));
		return [...cities.entries()]
			.sort((a, b) => b[1].length !== a[1].length ? b[1].length - a[1].length : a[0].localeCompare(b[0]))
			.map(([city, list]) => ({
				id: city === '' ? 'no-city' : city,
				label: city === '' ? 'No city' : city,
				items: list.map(p => ({ id: String(p.companyHotelId), label: shortName(p, scope.companyName) }))
					.sort((a, b) => a.label.localeCompare(b.label)),
			}));
	}, [scope]);

	return <Drawer title="Property groups" size="huge" ref={drawer.ref} onCloseRequest={() => ask({ kind: 'close' })}
		actions={<FormButton state="primary" isLoading={save.isPending} disabled={!canSave}
			onClick={() => save.mutate({ id: editing?.id ?? null, title: clean, companyHotelIds: selected, subtotalOnAll })}>
			{editing === null ? 'Add group' : 'Save group'}
		</FormButton>}>
		<div className="flex flex-col gap-5 px-4 py-6 sm:px-6">
			<Message state="info" flush>
				The Property groups belong to {scope.companyName}. Each BI user sees each group, with the properties that the user can read.
				One property can be in more than one group.
			</Message>
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
				<div className="flex flex-col gap-3 lg:border-r lg:border-gray-200 lg:pr-6">
					<SelectList ariaLabel="Property groups" empty="No group yet" value={editing === null ? null : String(editing.id)}
						options={groups.map(g => ({ id: String(g.id), label: g.title, detail: propertyCount(g.companyHotelIds.length) }))}
						onChange={key => key !== current && ask({ kind: 'switch', key })} />
					<div>
						<FormButton state="white" size="small" icon={<PlusIcon aria-hidden="true" className="h-4 w-4" />}
							onClick={() => current !== NEW && ask({ kind: 'switch', key: NEW })}>New group</FormButton>
					</div>
				</div>
				<section aria-label={editing === null ? 'New group' : editing.title} className="flex flex-col gap-5 lg:col-span-2">
					<div className="flex flex-wrap items-end justify-between gap-3">
						<div className="w-full max-w-sm">
							<FormInputText form={form} name="title" label="Name" placeholder="For example Rome" maxLength={MAX_TITLE} isRequired />
						</div>
						{editing !== null && <FormButton state="white" size="small" icon={<TrashIcon aria-hidden="true" className="h-4 w-4 text-gray-500" />}
							onClick={() => deleteDialogue.open()}>Delete group</FormButton>}
					</div>
					{duplicate && <Message state="error" flush>Another group has this name. Give this group another name.</Message>}
					<FormCheckbox form={form} name="subtotalOnAll" label="Subtotal on All properties (Pick-up monitoring)" />
					{overlaps.length > 0 && <Message state="error" flush>
						{overlaps.map(o => `${nameOf(o.companyHotelId)} is already in the subtotal group ${o.group}.`).join(' ')} A property can be in one
						subtotal group only: take it out of this group, or clear the mark of the other group.
					</Message>}
					<div className="flex items-baseline justify-between gap-3">
						<p className="text-sm font-semibold text-gray-900">Properties</p>
						<p className="text-sm text-gray-500">{selected.length} of {scope.properties.length} selected</p>
					</div>
					<GroupedChecklist groups={checklist} selected={selected.map(String)} onChange={ids => setSelected(ids.map(Number))} />
				</section>
			</div>
		</div>
		<Dialogue ref={discardDialogue.ref} title="Discard the changes?" size="small" confirmButtonName="Discard changes"
			cancelButtonName="Keep editing" onConfirm={discard} onCancel={() => { pending.current = null; }}>
			<p className="text-sm text-gray-500">RoomPulse drops the changes to this group. The saved groups stay as they are.</p>
		</Dialogue>
		<Dialogue ref={deleteDialogue.ref} title={`Delete the group “${editing?.title ?? ''}”?`} size="small"
			confirmButtonName="Delete group" cancelButtonName="Cancel" onConfirm={() => {
				if (editing === null) return;
				deleted.current = editing.title;
				remove.mutate(editing.id);
			}}>
			<p className="text-sm text-gray-500">The properties stay in RoomPulse. A link to this group opens All properties.</p>
		</Dialogue>
	</Drawer>;
});

export default PropertyGroupsDrawer;
